import React from "react"
import { useNavigate } from "react-router"
import useSWR from "swr"
import { Bell, MessageSquare, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover"
import { useTranslations } from "@/hooks/useTranslations"
import { cn } from "@/lib/utils"
import { getAuthHeaders } from "@/lib/apiUtils"

interface Notification {
	id: string
	recipientId: string
	senderId: string
	type: string
	title: string
	message: string
	entityType: string
	entityId: string
	organizationId: string
	metadata: {
		expedientTitle?: string
		authorName?: string
	} | null
	isRead: boolean
	readAt: string | null
	createdAt: string
	updatedAt: string
}

const fetcher = async (url: string) => {
	const headers = await getAuthHeaders()
	const r = await fetch(url, { headers })
	if (!r.ok) return []
	const data = await r.json()
	return Array.isArray(data) ? data : []
}

function formatTimeAgo(dateStr: string, t: ReturnType<typeof useTranslations>): string {
	const diff = Date.now() - new Date(dateStr).getTime()
	const minutes = Math.floor(diff / 60000)
	if (minutes < 1) return t("tenderAssistant.now")
	if (minutes < 60) return `${minutes}m`
	const hours = Math.floor(minutes / 60)
	if (hours < 24) return `${hours}h`
	const days = Math.floor(hours / 24)
	return `${days}d`
}

export function NotificationBell() {
	const router = useNavigate()
	const t = useTranslations()
	const [open, setOpen] = React.useState(false)

	const { data, mutate } = useSWR<Notification[]>(
		"/api/notifications?unreadOnly=true",
		fetcher,
		{
			refreshInterval: 15000,
			revalidateOnFocus: true,
		}
	)

	const unreadNotifications = data ?? []
	const unreadCount = unreadNotifications.length

	const handleNotificationClick = async (notif: Notification) => {
		// Mark as read
		getAuthHeaders().then((headers) =>
			fetch(`/api/notifications/${notif.id}`, {
				method: "PATCH",
				headers,
			})
		).then(() => mutate())

		// Navigate to the expedient communication tab
		if (notif.entityType === "expedient" && notif.entityId) {
			router(`/expedients/${notif.entityId}?tab=communication`)
		}

		setOpen(false)
	}

	const handleMarkAllRead = async () => {
		const headers = await getAuthHeaders()
		await fetch("/api/notifications/mark-all-read", {
			method: "POST",
			headers,
		})
		mutate()
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative h-9 w-9 rounded-full hover:bg-institutional/5"
				>
					<Bell className="h-4.5 w-4.5 text-muted-foreground" />
					{unreadCount > 0 && (
						<span className="absolute -top-0.5 -right-0.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-sm">
							{unreadCount > 9 ? "9+" : unreadCount}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				className="w-80 p-0"
				sideOffset={8}
			>
				{/* Header */}
				<div className="flex items-center justify-between px-4 py-2.5 border-b">
					<h4 className="text-sm font-semibold">
						{t("notifications.title")}
					</h4>
					{unreadCount > 0 && (
						<button
							onClick={handleMarkAllRead}
							className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
						>
							<Check className="h-3 w-3" />
							{t("notifications.markAllRead")}
						</button>
					)}
				</div>

				{/* Notification list */}
				<div className="max-h-80 overflow-y-auto">
					{unreadCount === 0 ? (
						<div className="flex flex-col items-center justify-center py-8 text-center">
							<Bell className="h-8 w-8 text-muted-foreground/30 mb-2" />
							<p className="text-sm text-muted-foreground">
								{t("notifications.noNotifications")}
							</p>
						</div>
					) : (
						unreadNotifications.map((notif) => (
							<button
								key={notif.id}
								onClick={() => handleNotificationClick(notif)}
								className="flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border/40 last:border-b-0"
							>
								<div className="h-8 w-8 rounded-full bg-[#3d7a4f]/10 flex items-center justify-center shrink-0 mt-0.5">
									<MessageSquare className="h-4 w-4 text-[#3d7a4f]" />
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-[13px] font-medium text-foreground">
										{notif.title}
									</p>
									<p className="text-xs text-muted-foreground truncate mt-0.5">
										{notif.metadata?.expedientTitle || notif.message}
									</p>
									<p className="text-[11px] text-muted-foreground/60 mt-1">
										{formatTimeAgo(notif.createdAt, t)}
									</p>
								</div>
								<div className="h-2 w-2 rounded-full bg-[#3d7a4f] shrink-0 mt-2" />
							</button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
